import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { getMovieById, getRelatedMovies, formatViews } from "@/utils/movies";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [toast, setToast] = useState("");
  const movie = id ? getMovieById(id) : undefined;

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          影片不存在或已下架
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          你访问的影片资料可能已被移除，试试回到片库继续浏览。
        </p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回片库
        </Link>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 4);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "分类", to: "/category" }, { label: movie.title }]}
      />

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
        >
          <i className="ri-arrow-left-line" />
          返回
        </button>
        <button
          type="button"
          onClick={handleShare}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
        >
          <i className="ri-share-forward-line" />
          分享链接
        </button>
      </div>

      <article className="mt-6">
        <header>
          <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            {movie.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              {movie.region}
            </span>
            {movie.genres.map((g) => (
              <span
                key={g}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
              >
                {g}
              </span>
            ))}
            <span className="text-foreground-500">{movie.year}</span>
            <span className="flex items-center gap-1 text-accent-600">
              <i className="ri-star-fill" />
              {movie.rating.toFixed(1)} 分
            </span>
          </div>
        </header>

        <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
          <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200 md:w-[300px] md:flex-none">
            <img
              src={movie.cover}
              alt={`${movie.title}影片海报`}
              title={`${movie.title}剧情介绍与详情 - 日韩伦理片`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <dl className="grid grid-cols-1 gap-x-8 gap-y-4 rounded-lg border border-background-200 bg-background-50 p-4 md:grid-cols-2 md:p-6">
              <div>
                <dt className="text-xs text-foreground-500">导演</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.director}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">上映时间</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.releaseDate}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">片长</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.duration} 分钟</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">热度</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{formatViews(movie.views)} 次浏览</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-xs text-foreground-500">主演</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">
                  {movie.cast.join(" / ")}
                </dd>
              </div>
            </dl>

            <div className="mt-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-primary-500" />
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
                {movie.synopsis}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-start gap-3 rounded-lg border border-accent-300 bg-accent-50 p-4 md:p-5">
          <i className="ri-information-line mt-0.5 text-lg text-accent-700" />
          <div>
            <h2 className="font-heading text-sm font-semibold text-accent-900">版权与内容说明</h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground-700 md:text-sm">
              本页面仅为影片资料的剧情介绍与信息展示，<strong>不提供任何在线播放、下载或资源链接</strong>。
              所涉片名、主创与简介均为示例信息，仅用于内容展示与交流。
            </p>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-10">
          <div className="mb-5 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-movie-2-line" />
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关推荐
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-md bg-primary-950 px-5 py-3 text-sm text-background-50 shadow-lg animate-fade-up">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-accent-400" />
            {toast}
          </span>
        </div>
      )}
    </div>
  );
}